/**
 * BulkActionStepperModal
 *
 * Stepper modal used for the bulk Reject and Request-More-Info flows. Renders
 * one form per selected work order so the user supplies a per-job comment
 * before a single POST ships the whole batch.
 *
 * Navigation: clickable dots (for up to 8 jobs) or a progress bar (for more)
 * let the user jump freely between steps. Answers persist across navigation
 * because react-hook-form `useFieldArray` holds all steps in the same form.
 *
 * On submit the caller receives the ordered `items` array and is responsible
 * for wiring it into the appropriate mutation.
 */

import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  File as FileIcon,
  Loader2,
  Upload,
  X,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

export interface BulkActionJob {
  id: number;
  name: string | null;
  address: string | null;
  work_order_status: string | null;
  req_completion_date: string | null;
}

export type BulkActionMode = 'reject';

/**
 * Shape the stepper emits on submit. Kept as a local type so this component
 * doesn't couple to the contracts package — the comment shape lines up with
 * `BulkJobInput` in useWorkOrderActions, which maps to the backend's richer
 * `rejections` / `requests` payloads. `files` is not yet sent to the backend;
 * the caller logs them until S3 attachment upload is wired up.
 */
export interface BulkActionStepperSubmitItem {
  id: number;
  comments: string;
  files: File[];
}

interface BulkActionStepperModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: BulkActionMode;
  jobs: BulkActionJob[];
  isSubmitting: boolean;
  onSubmit: (items: BulkActionStepperSubmitItem[]) => void | Promise<void>;
}

const stepSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.coerce.number().int(),
        comments: z.string().trim().min(1, 'Required').max(500, 'Maximum 500 characters'),
      }),
    )
    .min(1),
});

type StepFormValues = z.infer<typeof stepSchema>;

const DOT_THRESHOLD = 8; // Switch to a progress bar once jobs exceed this count

const MODE_CONFIG: Record<
  BulkActionMode,
  {
    title: (count: number) => string;
    description: string;
    commentLabel: string;
    commentPlaceholder: string;
    submitLabel: string;
    submitClass: string;
    icon: typeof XCircle;
    iconClass: string;
    iconBg: string;
  }
> = {
  reject: {
    title: (count) => `Reject ${count} job${count === 1 ? '' : 's'}`,
    description:
      'Provide a rejection reason for each job. You can move between jobs freely — answers are preserved.',
    commentLabel: 'Rejection comments',
    commentPlaceholder: 'Why is this job being rejected?',
    submitLabel: 'Submit rejections',
    submitClass: 'bg-red-600 hover:bg-red-700 text-white',
    icon: XCircle,
    iconClass: 'text-red-600',
    iconBg: 'bg-red-50 dark:bg-red-950',
  },
};

export function BulkActionStepperModal({
  open,
  onOpenChange,
  mode,
  jobs,
  isSubmitting,
  onSubmit,
}: BulkActionStepperModalProps) {
  const config = MODE_CONFIG[mode];
  const [step, setStep] = useState(0);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Per-step File[] attachments. Kept outside react-hook-form because File
  // objects aren't easy to validate with zod and we don't need rhf's change
  // tracking for them — the stepper doesn't block navigation on files.
  const [filesByStep, setFilesByStep] = useState<File[][]>(() => jobs.map(() => []));
  const [isDragging, setIsDragging] = useState(false);

  const defaultValues = useMemo<StepFormValues>(
    () => ({ items: jobs.map((job) => ({ id: job.id, comments: '' })) }),
    [jobs],
  );

  const {
    control,
    trigger,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<StepFormValues>({
    resolver: zodResolver(stepSchema),
    defaultValues,
    mode: 'onChange',
  });

  const { fields } = useFieldArray({ control, name: 'items' });

  // Only reset on the false→true open transition. The parent re-renders on
  // every `isSubmitting` change and passes a freshly-mapped `jobs` array each
  // time, so tying this effect to `jobs` / `defaultValues` would wipe the
  // user's answers mid-submission. Reading them from the closure keeps us
  // honest without reacting to identity-only changes.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      reset(defaultValues);
      setFilesByStep(jobs.map(() => []));
      setStep(0);
      setIsDragging(false);
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const totalSteps = fields.length;
  const currentJob = jobs[step];
  const isLastStep = step === totalSteps - 1;

  // Live-watch comments so the dot fill state tracks typing without re-renders
  // from form context. Each entry is the trimmed comment length per step.
  const watchedItems = watch('items');
  const filledFlags = useMemo(
    () => (watchedItems ?? []).map((it) => (it?.comments?.trim().length ?? 0) > 0),
    [watchedItems],
  );

  const hasAnyAttachment = filesByStep.some((fs) => fs.length > 0);
  const hasAnyInput = filledFlags.some(Boolean) || hasAnyAttachment;

  const handleCloseRequested = () => {
    if (isSubmitting) return;
    if (hasAnyInput) {
      setShowCancelConfirm(true);
    } else {
      onOpenChange(false);
    }
  };

  const confirmCancel = () => {
    setShowCancelConfirm(false);
    onOpenChange(false);
  };

  const handleNext = async () => {
    const ok = await trigger(`items.${step}.comments`);
    if (!ok) return;
    if (step < totalSteps - 1) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleDotClick = async (index: number) => {
    if (index === step) return;
    // If moving forward, require the current step to be valid so we don't
    // leave "holes" behind. Moving backward is always allowed — answers persist.
    if (index > step) {
      const ok = await trigger(`items.${step}.comments`);
      if (!ok) return;
    }
    setStep(index);
  };

  const onFormSubmit = handleSubmit(async (values) => {
    // Zip comments with files per step, trimming comments before sending.
    await onSubmit(
      values.items.map((it, index) => ({
        id: it.id,
        comments: it.comments.trim(),
        files: filesByStep[index] ?? [],
      })),
    );
  });

  /** Append files to the current step's attachment list, de-duping by name + size. */
  const appendFilesToCurrentStep = (incoming: File[]) => {
    if (incoming.length === 0) return;
    setFilesByStep((prev) => {
      const next = [...prev];
      const existing = next[step] ?? [];
      const seen = new Set(existing.map((f) => `${f.name}:${f.size}`));
      const deduped = incoming.filter((f) => !seen.has(`${f.name}:${f.size}`));
      next[step] = [...existing, ...deduped];
      return next;
    });
  };

  const removeFileFromCurrentStep = (fileIndex: number) => {
    setFilesByStep((prev) => {
      const next = [...prev];
      const existing = next[step] ?? [];
      next[step] = existing.filter((_, i) => i !== fileIndex);
      return next;
    });
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    appendFilesToCurrentStep(Array.from(e.target.files ?? []));
    // Reset so the same file can be picked again after removal.
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    appendFilesToCurrentStep(Array.from(e.dataTransfer.files ?? []));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const currentStepFiles = filesByStep[step] ?? [];

  if (jobs.length === 0) return null;

  const Icon = config.icon;

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) handleCloseRequested();
          else onOpenChange(true);
        }}
      >
        <DialogContent
          // NOTE: do NOT add `relative` here — shadcn's DialogContent is
          // `fixed` with a translate-based centering, and `relative` would be
          // merged away by tailwind-merge and break the centering. `fixed`
          // still establishes a containing block so our `absolute inset-0`
          // loading overlay below positions correctly against the modal.
          className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col gap-0 p-0"
          onInteractOutside={(e) => {
            // Block outside-click dismissal while submitting.
            if (isSubmitting) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (isSubmitting) e.preventDefault();
          }}
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
            <div className="flex items-start gap-3">
              <div
                className={`w-10 h-10 rounded-lg ${config.iconBg} flex items-center justify-center shrink-0`}
              >
                <Icon className={`w-5 h-5 ${config.iconClass}`} />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle>{config.title(totalSteps)}</DialogTitle>
                <DialogDescription className="mt-1">{config.description}</DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Step indicators */}
          <div className="px-6 py-4 border-b border-border bg-muted/30">
            {totalSteps > DOT_THRESHOLD ? (
              <ProgressBar current={step} total={totalSteps} />
            ) : (
              <Dots
                total={totalSteps}
                current={step}
                filledFlags={filledFlags}
                onClick={handleDotClick}
              />
            )}

            {currentJob && (
              <div className="mt-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-foreground truncate">
                    {currentJob.name ?? `Job #${currentJob.id}`}
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0">
                    Job {step + 1} of {totalSteps}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                  {[
                    currentJob.work_order_status,
                    currentJob.address,
                    currentJob.req_completion_date
                      ? `Due ${new Date(currentJob.req_completion_date).toLocaleDateString(
                          'en-GB',
                          { day: 'numeric', month: 'short', year: 'numeric' },
                        )}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
            )}
          </div>

          {/* Per-job form body */}
          <form onSubmit={onFormSubmit} className="flex-1 overflow-y-auto">
            <div className="px-6 py-5 space-y-5">
              {fields.map((field, index) => {
                // Only render the active step; keep the others mounted via
                // Controller registration so values are preserved by rhf.
                const isActive = index === step;
                return (
                  <div key={field.id} style={{ display: isActive ? 'block' : 'none' }}>
                    <Controller
                      control={control}
                      name={`items.${index}.comments`}
                      render={({ field: controllerField }) => (
                        <div className="space-y-2">
                          <Label htmlFor={`comments-${index}`}>
                            {config.commentLabel} <span className="text-red-600">*</span>
                          </Label>
                          <Textarea
                            id={`comments-${index}`}
                            {...controllerField}
                            placeholder={config.commentPlaceholder}
                            rows={6}
                            className="resize-none"
                            maxLength={500}
                            autoFocus={isActive}
                          />
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-red-600">
                              {errors.items?.[index]?.comments?.message ?? ''}
                            </span>
                            <span className="text-muted-foreground">
                              {controllerField.value?.length ?? 0} / 500
                            </span>
                          </div>
                        </div>
                      )}
                    />
                  </div>
                );
              })}

              {/* Supporting documentation — per step. Files are captured in
                  state but not yet uploaded to S3; the caller logs them with
                  a TODO until attachment upload is wired up. */}
              <div className="space-y-2">
                <Label>Supporting documentation (optional)</Label>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragging
                      ? 'border-[#E84A2B] bg-orange-50 dark:bg-orange-950/20'
                      : 'border-border hover:border-[#E84A2B] hover:bg-muted/50'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <Upload className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <p className="text-sm text-foreground">
                      Drop files here or{' '}
                      <label className="text-[#E84A2B] hover:underline cursor-pointer">
                        browse
                        <input
                          type="file"
                          className="hidden"
                          multiple
                          onChange={handleFileInputChange}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                        />
                      </label>
                    </p>
                    <p className="text-xs text-muted-foreground">PDF, Word, Excel, or images</p>
                  </div>
                </div>

                {currentStepFiles.length > 0 && (
                  <ul className="space-y-2 mt-2">
                    {currentStepFiles.map((file, fileIndex) => (
                      <li
                        key={`${file.name}-${file.size}-${fileIndex}`}
                        className="flex items-center gap-3 p-2 rounded border border-border bg-muted/30"
                      >
                        <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center shrink-0">
                          <FileIcon className="w-4 h-4 text-[#E84A2B]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground truncate">{file.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(file.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="shrink-0 h-8 w-8"
                          onClick={() => removeFileFromCurrentStep(fileIndex)}
                          aria-label={`Remove ${file.name}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </form>

          <DialogFooter className="px-6 py-4 border-t border-border bg-muted/30 sm:justify-between">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={handleCloseRequested}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={step === 0 || isSubmitting}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              {isLastStep ? (
                <Button
                  type="button"
                  className={config.submitClass}
                  onClick={() => void onFormSubmit()}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting…' : config.submitLabel}
                </Button>
              ) : (
                <Button
                  type="button"
                  className="bg-[#E84A2B] hover:bg-[#E84A2B]/90 text-white"
                  onClick={() => void handleNext()}
                  disabled={isSubmitting}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </DialogFooter>

          {/*
            Submission overlay.
            Covers the entire DialogContent (including the built-in close X and
            the form body) so NO interaction reaches the underlying modal while
            the mutation is in flight. z-20 sits above shadcn's internal
            children; aria-busy announces the busy state to assistive tech.
          */}
          {isSubmitting && (
            <div
              className="absolute inset-0 z-20 flex items-center justify-center bg-background/75 backdrop-blur-sm"
              role="status"
              aria-busy="true"
              aria-live="polite"
            >
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 text-[#E84A2B] animate-spin" />
                <p className="text-sm font-medium text-foreground">
                  {`Submitting ${totalSteps} ${totalSteps === 1 ? 'rejection' : 'rejections'}…`}
                </p>
                <p className="text-xs text-muted-foreground">
                  Please wait, this can take a moment.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Nested confirm for discarding typed answers. Kept inline so the parent
          stepper stays behind it (rather than opening a sibling dialog). */}
      <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-950 flex items-center justify-center shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <DialogTitle>Discard changes?</DialogTitle>
                <DialogDescription className="mt-1">
                  You have unsaved comments. Closing now will lose them.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelConfirm(false)}>
              Keep editing
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmCancel}>
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Dots({
  total,
  current,
  filledFlags,
  onClick,
}: {
  total: number;
  current: number;
  filledFlags: boolean[];
  onClick: (index: number) => void;
}) {
  return (
    <div
      className="flex items-center justify-center gap-2"
      role="tablist"
      aria-label="Step progress"
    >
      {Array.from({ length: total }).map((_, i) => {
        const isCurrent = i === current;
        const isFilled = filledFlags[i];
        return (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={isCurrent}
            aria-label={`Go to job ${i + 1} of ${total}${isFilled ? ' (completed)' : ''}`}
            onClick={() => onClick(i)}
            className={`h-3 w-3 rounded-full border transition-all ${
              isCurrent
                ? 'border-[#E84A2B] bg-[#E84A2B] ring-2 ring-[#E84A2B]/30 scale-110'
                : isFilled
                  ? 'border-green-600 bg-green-600 hover:bg-green-700'
                  : 'border-border bg-background hover:bg-muted'
            }`}
          >
            {isFilled && !isCurrent && (
              <Check className="w-2 h-2 text-white mx-auto pointer-events-none" strokeWidth={4} />
            )}
          </button>
        );
      })}
    </div>
  );
}

function ProgressBar({ current, total }: { current: number; total: number }) {
  const percent = Math.round(((current + 1) / total) * 100);
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
        <span>
          Job {current + 1} of {total}
        </span>
        <span>{percent}%</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-[#E84A2B] transition-all" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}
